import { AppConfig, InitDatadogOptions } from './models';
import { datadogRum, RumInitConfiguration, DefaultPrivacyLevel } from '@datadog/browser-rum';
import { PrivacyOptions } from '../../enums/Datadog';

export const actionNameAttribute = 'data-dd-action';
const defaultActionName = 'discard-action';

/**
 * init datadog function
 * @example {
 *    initDataDog( {
 *       applicationId: '***',
 *       clientToken: '***',
 *       service: 'my sunbit application',
 *       env: window.sunbitEnv,
 *       version: '1.0.0',
 *     },
 *     {
 *        disableSessionReplayRecording: false // optionally disable sessionsReplayRecord
 *     }
 *    )
 */
export function initDataDog(appConfig: AppConfig, options: InitDatadogOptions = {}) {
  const config: RumInitConfiguration = {
    sampleRate: 100,
    trackInteractions: true,
    trackFrustrations: true,
    ...appConfig,
    // forcing below properties
    site: 'datadoghq.com',
    actionNameAttribute,
    defaultPrivacyLevel: (options.defaultPrivacyLevel || 'mask') as DefaultPrivacyLevel,
    beforeSend: (event) => {
      // Discard actions that have the default action name.
      if (event?.type === 'action' && event?.action?.target?.name === defaultActionName) return false;
    },
  };

  const bodyElement = document.getElementsByTagName('body')[0];
  if (!bodyElement) throw 'Error: Could not find body element. datadog is not initialized';
  bodyElement.setAttribute(actionNameAttribute, defaultActionName);

  datadogRum.init(config);

  !options.disableSessionReplayRecording && datadogRum.startSessionReplayRecording();

  if (!appConfig.env.includes('prod')) {
    const comment = document.createComment(`${actionNameAttribute} attribute has been injected automatically`);
    bodyElement.appendChild(comment);
    console.log('Datadog config: ', datadogRum.getInitConfiguration());
  }
}

export const privacyOptionsClassNames: { [keys in PrivacyOptions]: string } = {
  [PrivacyOptions.Allow]: 'dd-privacy-allow',
  [PrivacyOptions.Mask]: 'dd-privacy-mask',
  [PrivacyOptions.Hidden]: 'dd-privacy-hidden',
  [PrivacyOptions.MaskUserInput]: 'dd-privacy-mask-user-input',
};

export { datadogRum } from '@datadog/browser-rum';
